/**
 * The first-run panel's one read.
 *
 *   fetchOnboarding   GET /onboarding   the five setup facts, as one union
 *
 * **Why this is not five more fields on `GET /overview`.** Every fact behind it
 * belongs to a screen the dashboard already links to, and folding them into the
 * dashboard response was the obvious build. Three things stop it:
 *
 *  1. **Slice 10 refused inventory on that response on purpose**, and
 *     `overviewResponseSchema` is `.strict()` so that the refusal is enforced
 *     rather than remembered. Adding five booleans there would reopen a decision
 *     by accident — and the fifth person to add "just one more" field is how a
 *     dashboard becomes 70% inventory, which is the failure mode
 *     `routes/HomePage.tsx` is built against.
 *  2. **The panel has to be mountable somewhere else.** Slice 20 rebuilds the
 *     Overview as a dashboard and the checklist may not survive that layout in
 *     the same place; slices 16 and 17 may want it on Integrations while a
 *     workspace is still empty. A component that can only render on the one
 *     screen where another endpoint was already fetched is not a component that
 *     can move, whatever its props say.
 *  3. **A failure here must not blank the bounce and complaint rates.** That is
 *     the argument `overview.ts` makes for splitting engagement out, and it
 *     applies with less drama but the same shape: a checklist that could not
 *     load is a nuisance, and the two rates that can cost a tenant their AWS
 *     account are the reason the Overview exists.
 *
 * The cost, stated: two requests are two moments. It is affordable because
 * nothing on this response is compared arithmetically with anything on the
 * dashboard — five booleans about whether a thing exists at all, read a few
 * milliseconds apart from counts about sending, cannot be made to disagree.
 * There is no `generatedAt` on it for the same reason; the contract's header
 * says why an unrendered timestamp would be the page-filling this slice removes.
 *
 * **It reaches no provider and could not.** All five facts are `EXISTS` over the
 * tenant's own tables under RLS. In particular step 1 says an SES account is
 * *connected*, not that it can reach anybody — sandbox state, verified
 * identities and quota are the Overview's `sendability`, computed from the
 * stored `last_check`, and two surfaces answering "can you send" separately is
 * how they come to disagree.
 *
 * No query, no paging, no filter, and none belongs here: the response is a
 * two-arm union over a fixed five-step path, so there is nothing to narrow.
 *
 * Parsed against the contract like every other read in this directory, and here
 * that parse earns its keep twice over. `onboardingResponseSchema` is not a
 * shape check — its refinement rejects steps out of order, a `next` that is not
 * the first undone step, and a `complete` arm that arrived carrying steps. An
 * API that got any of those wrong fails here, loudly, instead of rendering a
 * checklist that quietly points at the wrong thing to do next.
 */
import { onboardingResponseSchema, type OnboardingResponse } from '@wend/shared';

import { apiRequest } from './client';

/**
 * Either there is a path left to walk, or there is not.
 *
 * The `complete` arm carries no steps, so a caller cannot render a checklist for
 * a finished workspace even by mistake — see `OnboardingPanel`, which returns
 * `null` on it.
 */
export async function fetchOnboarding(signal?: AbortSignal): Promise<OnboardingResponse> {
  return apiRequest({
    path: '/onboarding',
    schema: onboardingResponseSchema,
    signal,
  });
}
